import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { CalendarCheck } from 'lucide-react'
import { usePOSStore, DEFAULT_UI_FILTERS, isReceiptPartiallyPaid } from '@/stores/posStore'
import { useUIStore } from '@/stores/uiStore'
import { useBrands, useCategories, useInfiniteProducts, productKeys, fetchProductBatches, isSyntheticLotKey } from '@/api/products'
import { lookupReceiptByNumber, useCompletePickup } from '@/api/receipts'
import type { ProductLot } from '@/types'
import { v2, v2api, V2ApiError } from '@/api/v2'
import { usePOSConfig } from '@/api/config'
import { useQueryClient } from '@tanstack/react-query'
import { useIsMobile, useDebounce, useBarcodeScanner, useSelectCustomer, type ScanMeta } from '@/hooks'
import { useV2PreviewBridge } from '@/hooks/useV2PreviewBridge'
import { useBoundReceiptReconcile } from '@/hooks/useBoundReceiptReconcile'
import { useRefundCartMutations } from '@/hooks/useRefundCartMutations'
import { useAgeRestriction } from '@/hooks/useAgeRestriction'
import { Layout, Header, TabsBar } from '@/components/layout'
import { LeftSidebar } from '@/components/sidebar'
import { RightSidebar, CheckoutModal } from '@/components/cart'
import { MobileCartBar } from '@/components/cart/MobileCartBar'
import { ScanModal } from '@/components/cart/ScanModal'
import { ProductGrid, CategoriesBar, ProductFiltersBar, ProductModal } from '@/components/products'
import { CustomersTab, CustomerModal, type CustomerModalTabId } from '@/components/customers'
import { OrdersTab } from '@/components/orders'
import { OrderDetailModal } from '@/components/orders/OrderDetailModal'
import { FunctionsTab } from '@/components/functions'
import { ConfirmModal, AgeVerifyModal, LotSelectModal, HeldReceiptsModal, VoidReceiptModal, DiscountModal, PayoutModal, GiftCardSellModal, BarcodeMatchModal } from '@/components/modals'
import { OfflineIndicator } from '@/lib/offline'
import { broadcastProductShown, toFeaturedProduct } from '@/lib/cartChannel'
import { effectiveUnitPrice } from '@/lib/pricing'
import { isSaleBlocked } from '@/lib/customerBlock'
import { parseGs1Barcode } from '@/lib/gs1'
import { pickScanConfirmTarget, findLotIgnoreCase } from '@/lib/batches'
import { useCartStore } from '@/stores/cartStore'
import { useRecentProductsStore } from '@/stores/recentProductsStore'
import type { Product, Customer } from '@/types'

export const Route = createFileRoute('/pos/')({
  component: POSMainPage,
})

function POSMainPage() {
  // One-shot ghost-cart reconcile: a register rehydrated with a receipt
  // that settled server-side while the UI was gone (logout/crash
  // mid-payment) clears itself instead of presenting a paid sale as an
  // open cart.
  useBoundReceiptReconcile()

  const {
    activeTab,
    viewMode,
    searchQuery,
    selectedCategoryId,
    uiFilters,
    cart,
    selectedCustomer,
    addToCart,
    changeQuantity,
    removeFromCart,
    setActiveTab,
    setSearchQuery,
    setSelectedCategory,
    setUIFilters,
  } = usePOSStore()

  // ID-check gate state. Read via selectors (not the destructure above)
  // so only the checkout handler re-renders when the flag flips.
  const ageChecked = usePOSStore((s) => s.ageChecked)
  const setAgeChecked = usePOSStore((s) => s.setAgeChecked)
  const ageRestriction = useAgeRestriction()

  const { activeModal, openModal, confirmOptions, showToast, showConfirm } = useUIStore()
  const clerkReauthRequired = useUIStore((s) => s.clerkReauthRequired)
  const selectCustomer = useSelectCustomer()
  const completePickup = useCompletePickup()
  const isMobile = useIsMobile()
  const queryClient = useQueryClient()
  // Cart grouping behavior. Mirrors BE `pos.receipt.group_items`: when off
  // each click yields its own row instead of bumping qty on an existing one.
  const { data: posConfig } = usePOSConfig()
  const groupItems = posConfig?.posGroupItems ?? false
  // v2 in-memory cart: every cart change is a local mutation; /cart/preview
  // is the only backend call the cart makes during build. The legacy
  // `useSyncReceiptTotals` hook (per-keystroke POST/PUT/DELETE on
  // /receipts/{id}/items) is intentionally NOT mounted here -- it's the
  // architecture we are replacing. Hold and Pay still go through their
  // existing v1 hooks for now; that migration is a follow-up PR (Step 5).
  useV2PreviewBridge()

  // Refund mode keeps the local cart in lock-step with the persisted
  // refund receipt: PUT on qty edits, DELETE on line removals. Without
  // this, removing a line from the FE leaves the DB receipt unchanged
  // and Process Refund commits the original full amount. The hook
  // returns refund-aware versions of changeQuantity / removeFromCart
  // plus an isPending flag the summary and the Process Refund button
  // gate on (so totals never lag and the cashier can't fire payment
  // against unconfirmed state). Outside refund mode the hook is inert.
  const isRefundMode = usePOSStore((s) => s.isRefundMode)
  const refundReceiptId = usePOSStore((s) => s.currentReceiptId)
  const refundMutations = useRefundCartMutations(
    isRefundMode ? refundReceiptId : null,
  )
  const cartChangeQuantity = isRefundMode
    ? refundMutations.onQuantityChange
    : changeQuantity
  const cartRemoveFromCart = isRefundMode
    ? refundMutations.onRemoveFromCart
    : removeFromCart

  // Lazy categories — only consulted when the user clicks a "Top category"
  // chip in the customer insights modal. Cached for an hour by the hook.
  const { data: categories = [] } = useCategories()
  const { data: brands = [] } = useBrands()

  // Selected product for modal
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  // Receipt lookup — populated when the scanner reads an RCP- QR. Drives
  // the OrderDetailModal mount at the bottom of this component.
  const [openReceipt, setOpenReceipt] = useState<{ receiptId: number; number: string } | null>(null)
  // Camera barcode scanner overlay (mobile). Opened from the bottom action
  // bar's scan button; decodes feed handleScan — the same barcode -> add-to-cart
  // path as the keyboard-wedge scanner.
  const [scannerOpen, setScannerOpen] = useState(false)
  // Selected customer for the customer-details modal. `initialTab`
  // lets entry points (like the cart-header reward icon) jump straight
  // to a specific tab; falls back to the modal's own default
  // ('insights') when omitted.
  const [detailsCustomer, setDetailsCustomerState] = useState<Customer | null>(null)
  const [detailsInitialTab, setDetailsInitialTab] = useState<CustomerModalTabId | undefined>(undefined)
  const setDetailsCustomer = (
    customer: Customer | null,
    options?: { initialTab?: CustomerModalTabId },
  ) => {
    setDetailsCustomerState(customer)
    setDetailsInitialTab(options?.initialTab)
  }

  // Debounce the search input so we don't fire a request per keystroke.
  const debouncedSearchQuery = useDebounce(searchQuery, 300)

  // Fetch products with infinite scroll
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteProducts({
    search: debouncedSearchQuery || undefined,
    category_group_id: selectedCategoryId ?? undefined,
    uiFilters,
  })

  // Flatten pages into single product array
  const products = useMemo(() => {
    return data?.pages.flatMap(page => page.items) ?? []
  }, [data])

  // Build cart quantities map for ProductGrid
  const cartQuantities = useMemo(() => {
    const map = new Map<number, number>()
    cart.forEach(item => {
      const current = map.get(item.product_id) || 0
      map.set(item.product_id, current + item.quantity)
    })
    return map
  }, [cart])

  const handleProductClick = (product: Product, cartItemId?: string) => {
    const productWithCartId = { ...product, cartItemId }
    setSelectedProduct(productWithCartId)
  }

  // Returns true when the item was actually added, false when blocked
  // (currently only by the partial-payment gate). Callers that show a
  // success toast MUST gate it on this return value; otherwise the
  // cashier sees a contradictory "Added to Cart" toast for a no-op
  // after the blocked-by-partial-payment warning.
  // `scanCapsLock` rides along with `scannedLot` (wedge scans only):
  // CapsLock state at scan time orders findLotIgnoreCase's exact-match
  // probes so inverse-case duplicate lots resolve to the right one.
  const handleAddToCart = async (
    product: Product,
    scannedLot: string | null = null,
    scanCapsLock?: boolean,
  ): Promise<boolean> => {
    // Partial-payment gate. posStore.addToCart also silently early-
    // returns in this state (data-safety backstop for callers that
    // bypass this handler -- scan path, command palette, ProductModal
    // requantify), but we surface the toast HERE so the cashier knows
    // why their click didn't take. The same predicate gates the
    // CartItemRow +/-/X controls and the partial-payment banner in
    // RightSidebar -- single source of truth in posStore.
    {
      const { serverTotals, isRefundMode } = usePOSStore.getState()
      if (isReceiptPartiallyPaid(serverTotals, isRefundMode)) {
        showToast({
          type: 'warning',
          title: 'Receipt has payment(s) applied',
          message: 'Void the payment in Checkout before adding items.',
        })
        return false
      }
      // Refund mode takes no adds -- see the matching gate in
      // posStore.addToCart for why (an add turns the return into a sale
      // that can never settle, and abandoning it locks the source
      // receipt out of ever being refunded). Cashiers habitually scan
      // the returned package to check it in, which is exactly how the
      // corruption got in, so name that case in the message.
      if (isRefundMode) {
        showToast({
          type: 'warning',
          title: 'Refund in progress',
          message: 'Items can only be removed from a refund. Scanning does not add to a return — use the quantity controls instead.',
        })
        return false
      }
    }

    // Merchandising gate, mirrored from the till (waio-portal
    // ops_receipt._inactive_product_error): at a store that hides
    // inactive / flagged-out products from search, the register refuses
    // them too -- including scans, where the product never went through
    // the search filter. Refusing here gives the cashier a toast at the
    // scan; the server would otherwise drop the line at preview (the
    // cart then shows why it is missing from the total, but only after
    // the fact) and 400 the whole cart at commit.
    if (posConfig?.posHideOutOfStock && (product.is_inactive || product.out_of_stock)) {
      showToast({
        type: 'warning',
        title: product.is_inactive ? 'Inactive at this store' : 'Marked out of stock',
        message: `${product.display_name ?? product.description ?? 'This product'} cannot be sold here until the flag is cleared in the product settings.`,
      })
      return false
    }
    // Customer-aware batches cache key — must match the one
    // `useProductBatches` writes to so the modal reads back what we
    // just fetched here. The `useProductBatches` hook keys on
    // `customer_id` (so a customer change refetches with tier-priced
    // `discounted_price` per batch); without `customer_id` in the key
    // we'd write to a separate, stale cache and the modal would never
    // see this fetch.
    //
    // Mirrors `usePricingKey` resolution: cashier-selected customer
    // first, then the BE-resolved customer on the latest preview
    // (retail/guest fallback — e.g. id 3332). Without the preview
    // fallback, guest carts hit the batches endpoint with no
    // customer_id and the BE returns un-grouped catalog prices.
    const previewCustomerId = useCartStore.getState().preview?.customer_id ?? null
    const customerIdForBatches = selectedCustomer?.id ?? previewCustomerId ?? null
    const batchesQueryKey = [
      ...productKeys.detail(product.id),
      'batches',
      customerIdForBatches,
    ] as const

    // Lot resolution priority:
    //   1. `scannedLot` — extracted from a GS1 scan (handleScan path).
    //      The cashier scanned this exact lot off the package, so it
    //      wins over any cache- or FIFO-based guess.
    //   2. Cached FIFO pick — first available batch from a prior
    //      batches fetch. Optimistic; the async fetchQuery below
    //      patches the line if the cache turned out to be stale.
    //
    // Skip synthetic keys (BE returned an empty/duplicate batch_number)
    // so we don't optimistically pin a non-existent lot.
    const cachedBatches = queryClient.getQueryData<ProductLot[]>(batchesQueryKey)
    let knownLot: string | null = scannedLot
    if (knownLot !== null && cachedBatches && cachedBatches.length > 0) {
      // Adopt the stored batch casing when the scan matches one.
      // CapsLock inverts scanned letter case, and pinning the scanned
      // casing verbatim would submit a batch_number the journal doesn't
      // have — the BE nets on-hand case-sensitively (see
      // findLotIgnoreCase). Cache-miss adds get the same repair in the
      // async block below once the batch list lands.
      const canonical = findLotIgnoreCase(cachedBatches, knownLot, scanCapsLock)
      if (canonical?.lot_number) knownLot = canonical.lot_number
    }
    if (knownLot === null && cachedBatches && cachedBatches.length > 0) {
      const firstAvailable = cachedBatches.find((b) => b.quantity > 0)
      if (firstAvailable && !isSyntheticLotKey(firstAvailable.lot_number)) {
        knownLot = firstAvailable.lot_number
      }
    }

    // Optimistic add. With grouping on, addToCart bumps qty when a line
    // for the same (product, lot) already exists; with grouping off, every
    // call is a fresh row. Either way the UI updates synchronously.
    addToCart(product, 1, knownLot, { forceNewLine: !groupItems })

    // Customer-Facing Display: feature the just-added product on the
    // right panel of /customer. ProductModal already broadcasts when
    // open, but a + click on the grid (or sidebar / command palette) by-
    // passes the modal — without this call the CFD wouldn't update on
    // grid adds. Prefer /cart/preview's server-resolved unit price when
    // it lands; until then fall back to `effectiveUnitPrice` (same fn
    // posStore.addToCart uses for the optimistic shelf price) so the
    // CFD never shows the catalog regular price for a tier/sale-priced
    // line.
    const previewItems = useCartStore.getState().preview?.items ?? null
    const previewPrice: number | null = (() => {
      if (!previewItems) return null
      const m = previewItems.find((it) => it.product_id === product.id)
      if (!m) return null
      const n = parseFloat(String(m.price))
      return Number.isFinite(n) ? n : null
    })()
    const effectivePrice = previewPrice ?? effectiveUnitPrice(product, selectedCustomer)
    broadcastProductShown(toFeaturedProduct(product, effectivePrice))

    if (!product.is_cannabis) {
      // Non-cannabis lines never need a lot. Item is already added
      // above via addToCart() -- return success to the caller.
      return true
    }

    // ALWAYS fire fetchQuery for cannabis -- not conditional on knownLot.
    // fetchQuery respects the 60s staleTime: fresh cache hits return the
    // promise instantly with no network, while stale or missing cache
    // triggers a refetch. This is what gets us:
    //   - 1st click on a new SKU: network request fires
    //   - 2nd click within 60s: instant cache hit, no network
    //   - 1st click on a SKU that was persisted from a prior session:
    //     IDB has stale data, fetchQuery refetches because it's >60s old
    //
    // Without this, the IDB persister's restored cache was treated as
    // fresh forever, so the network call never fired for "new" products
    // that were actually present in the persisted snapshot.
    void (async () => {
      try {
        const batches = await queryClient.fetchQuery<ProductLot[]>({
          queryKey: batchesQueryKey,
          queryFn: () => fetchProductBatches(product.id, customerIdForBatches),
          staleTime: 60 * 1000,
        })
        if (!batches || batches.length === 0) return

        // If the cashier scanned a lot, the scanned value is canonical —
        // don't overwrite it with the FIFO pick. But when
        // `show_available_batches` is on we still validate: warn (don't
        // block) if the scanned lot isn't in the product's batch list.
        // V1 does the same via `pos/batch_apply`'s server-side check;
        // here we do it client-side off the already-fetched list.
        if (scannedLot !== null) {
          // Case-tolerant: CapsLock-inverted scans must still match
          // their batch (see findLotIgnoreCase).
          const canonical = findLotIgnoreCase(batches, scannedLot, scanCapsLock)

          if (canonical?.lot_number && canonical.lot_number !== knownLot) {
            // The scan matched a known batch under different casing than
            // we pinned (batch cache was cold at add time, so the raw
            // scan went on the line). Repair the line to the journal's
            // exact casing — submitting the scanned casing would sever
            // the sale from its batch in the BE's case-sensitive
            // on-hand netting.
            const { cart, changeBatch } = usePOSStore.getState()
            const pinned = [...cart].reverse().find(
              (it) =>
                it.product_id === product.id && it.lot_number === knownLot,
            )
            if (pinned) {
              changeBatch(pinned.id, canonical.lot_number)
            }
          }

          if (posConfig?.posShowAvailableBatches && !canonical) {
            console.warn('[scan] scanned lot not on product batch list', {
              product_id: product.id,
              scannedLot,
              available: batches.map((b) => b.lot_number),
            })
            showToast({
              type: 'warning',
              title: 'Lot Not in Inventory',
              message: `Lot "${scannedLot}" wasn't found on this product. Pinned anyway — verify against the package label.`,
            })
          }
          return
        }

        const firstAvailable = batches.find((b) => b.quantity > 0)
        if (!firstAvailable || isSyntheticLotKey(firstAvailable.lot_number)) return
        const lotFromServer = firstAvailable.lot_number

        // If the lot we optimistically pinned matches what came back,
        // nothing to do. Otherwise patch the line we just added.
        if (lotFromServer === knownLot) return

        // Find the line we just added: most recent matching product
        // whose lot is either unset or matches the (now-stale) optimistic
        // pick. Avoid touching older lines for the same product.
        const { cart, changeBatch } = usePOSStore.getState()
        const target = [...cart].reverse().find(
          (it) =>
            it.product_id === product.id
            && (it.lot_number === knownLot || it.lot_number === null),
        )
        if (target) {
          changeBatch(target.id, lotFromServer)
        }
      } catch (error) {
        console.error('Failed to fetch batches (optimistic add path):', error)
      }
    })()

    // Cannabis path: item is added synchronously via addToCart() above;
    // the IIFE above runs lot-resolution in the background. Return
    // success now rather than awaiting the background work.
    return true
  }

  // Wrapper for LeftSidebar that adds to cart instead of opening modal
  const handleAddToCartWrapper = async (product: Product) => {
    await handleAddToCart(product)
  }

  const handleChangeCustomer = () => {
    setActiveTab('customers')
  }

  const proceedToCheckout = () => {
    // Online-PAID pickup: the money is captured at the gateway and the cart
    // is a read-only view of the charged order. "Checkout" is rendered as
    // "Complete" (CartSummary) and must NOT open the tender modal — it runs
    // the single /complete-pickup transition and clears the register.
    {
      const meta = usePOSStore.getState().loadedEcomMeta
      const rid = usePOSStore.getState().currentReceiptId
      if (meta?.paidOnline && rid != null) {
        completePickup.mutate(
          { receiptId: rid },
          {
            onSuccess: () => {
              usePOSStore.getState().clearReceipt()
              showToast({
                type: 'success',
                title: 'Pickup completed',
                message: 'Order was paid online — no payment to take.',
              })
            },
            onError: (err) => {
              showToast({
                type: 'error',
                title: err instanceof V2ApiError && err.code === 'outside_sale_hours'
                  ? 'Outside Sale Hours'
                  : 'Could not complete pickup',
                message: err instanceof Error ? err.message : undefined,
              })
            },
          },
        )
        return
      }
    }
    // Price-confirmation HARD gate. Never open the tender modal unless
    // /cart/preview has CONFIRMED the current basket (previewStatus 'idle').
    // While 'stale'/'fetching' the totals are a local estimate; on 'error'
    // they're a shape-behind server snapshot (e.g. the qty-1 total still
    // showing after a qty-2 edit whose refresh failed). CheckoutModal prices
    // the tender straight off cartStore.preview.totals, so opening it while
    // unconfirmed lets the cashier charge a stale amount that disagrees with
    // the cart — exactly the "$69.58 cart, $34.79 till" divergence. The
    // Checkout button is gated identically; this guard is the source-level
    // backstop that also covers any non-button caller. Refund mode never
    // previews (serverTotals is authoritative), so it's exempt; the bridge's
    // self-heal retry recovers 'error' -> 'idle' on its own, re-enabling this.
    if (!isRefundMode) {
      // serverTotals (partial-payment continuation, unheld/committed receipt)
      // is BE-authoritative and drives the tender amount directly, so the
      // preview-confirmation gate only applies to a fresh cart priced off
      // /cart/preview. Without this exemption, reopening Checkout to collect
      // a remaining balance could be blocked when previewStatus isn't 'idle'.
      const { serverTotals } = usePOSStore.getState()
      const cs = useCartStore.getState()
      const ps = cs.previewStatus
      if (serverTotals === null && ps !== 'idle') {
        // On 'error', tailor the reason to what actually failed (the same
        // classification the banner uses) instead of asserting the server is
        // unreachable when the till is online. 'stale'/'fetching' is the
        // benign "still syncing" case.
        const info = ps === 'error' ? cs.previewError : null
        showToast({
          type: 'warning',
          title:
            info?.kind === 'rejected'
              ? 'Can\'t price this cart'
              : ps === 'error'
                ? 'Prices not confirmed'
                : 'Confirming prices…',
          message:
            ps !== 'error'
              ? 'Prices are still syncing with the server. Try again in a moment.'
              : info?.kind === 'rejected'
                ? (info.message
                    ? `Couldn't price this cart: ${info.message}. Review the items.`
                    : 'Couldn\'t price this cart. Review the items.')
                : info?.kind === 'offline'
                  ? 'No internet connection. Prices will confirm automatically when you\'re back online.'
                  : info?.kind === 'auth'
                    ? 'Reconnecting your session — try again in a moment.'
                    : 'Could not reach pricing — retrying automatically. Try again in a moment.',
        })
        return
      }
    }

    // Scan-to-confirm-lot HARD gate. When frontend.on_hold_scan_to_confirm_lot
    // is on, a hold/ecom order's cannabis lines load as scan_confirmed:false
    // and the cashier must scan each to confirm the physical lot before any
    // payment. Unlike the soft missing-lot prompt below, this can't be
    // acknowledged away — V1 likewise blocked the Pay button outright.
    if (posConfig?.posOnHoldScanToConfirmLot) {
      const unconfirmed = cart.filter((line) => line.scan_confirmed === false)
      if (unconfirmed.length > 0) {
        showToast({
          type: 'warning',
          title: 'Scan to Confirm Lot #',
          message: `Scan ${unconfirmed.length} more item${
            unconfirmed.length > 1 ? 's' : ''
          } to confirm lot numbers before payment.`,
        })
        return
      }
    }

    // Cannabis lot enforcement: when `frontend.show_available_batches`
    // is on, every cannabis line must have a lot before checkout. V1
    // hard-blocks the Pay button (`allBatchesConfirmed` returns false);
    // V2 lets the cashier confirm-and-pay so a missing lot label on a
    // legitimate scan doesn't deadlock a sale. The list of offending
    // lines is named in the prompt so the cashier knows what to fix
    // (or what they're acknowledging).
    if (posConfig?.posShowAvailableBatches) {
      const missingLot = cart.filter(
        (line) => line.product?.is_cannabis === true && !line.lot_number,
      )
      if (missingLot.length > 0) {
        const bulleted = missingLot
          .map(
            (l) =>
              `• ${
                l.product?.display_name
                || l.product?.description
                || `Product ${l.product_id}`
              }`,
          )
          .join('\n')
        const isPlural = missingLot.length > 1
        const heading = isPlural
          ? `Cannabis items missing Lot Numbers:`
          : `Cannabis item missing Lot Number:`
        showConfirm({
          title: 'Lot Number Missing',
          // Refund mode shares the same lot-enforcement check but the
          // cashier-facing wording should match the action they're
          // about to take. Without this swap, the prompt told the
          // cashier they were about to pay during a refund flow.
          message: `${heading}\n\n${bulleted}\n\n${
            isRefundMode
              ? 'Confirm to refund, or cancel to add Lot Numbers.'
              : 'Confirm to pay, or cancel to add Lot Numbers.'
          }`,
          confirmLabel: isRefundMode ? 'Confirm and Refund' : 'Confirm and Pay',
          cancelLabel: 'Add Lot Numbers',
          variant: 'danger',
          onConfirm: () => openModal('checkout'),
        })
        return
      }
    }
    openModal('checkout')
  }

  // ID-check gate. V1 ran `POS.STATE.ageCheck()` on every payment-button
  // click (pos.js clickPaymentButton / payment_id_check / the pinpad
  // `run`), blocking the tender until the cashier confirmed they'd asked
  // for ID. It short-circuits when the store has no age restriction, when
  // `pos.confirmation_of_age_limit` is off, or when the cashier already
  // confirmed for this transaction (`age_checked`, cleared on receipt
  // change — see posStore.clearCart / clearReceipt).
  //
  // Placed AHEAD of the price/lot gates rather than beside the
  // `openModal('checkout')` calls: one of those calls lives inside a
  // showConfirm `onConfirm`, and ConfirmModal nulls `confirmOptions`
  // immediately after invoking it, which would swallow a confirm opened
  // from within. Asking first also spares the cashier from confirming ID
  // only to be blocked by a missing lot.
  //
  // Refund mode is exempt: no product leaves the store, and the prompt
  // reads as a sale question. The lot/price gates below still apply to
  // refunds.
  const handleCheckout = () => {
    // Same gate as the Checkout button; this covers any non-button caller.
    const { loadedEcomMeta } = usePOSStore.getState()
    if (isSaleBlocked(selectedCustomer, {
      isRefundMode,
      isPaidOnlinePickup: Boolean(loadedEcomMeta?.paidOnline),
    })) {
      showToast({
        type: 'error',
        title: 'Customer Blocked',
        message: 'This customer can\'t complete a sale. Check their notes.',
      })
      return
    }

    // Fail CLOSED while the age-gate inputs are still loading. `age` and
    // `confirmationEnabled` both read as "no gate" before they resolve, so
    // without this a sale rung during the load window tenders with no
    // prompt at all. Blocking is safe here because it self-heals in well
    // under a second and only in the narrow case the cache didn't cover --
    // the same "try again in a moment" idiom the price-confirmation gate
    // below already uses. Refunds skip the ID check entirely, so they have
    // nothing to wait for.
    if (!isRefundMode && !ageRestriction.inputsResolved) {
      showToast({
        type: 'warning',
        title: 'Checking age restrictions…',
        message: 'Still loading store settings. Try again in a moment.',
      })
      return
    }

    const needsIdCheck =
      !isRefundMode
      && ageRestriction.age !== null
      && ageRestriction.confirmationEnabled
      && !ageChecked

    if (!needsIdCheck) {
      proceedToCheckout()
      return
    }

    showConfirm({
      title: 'ID Check Required',
      // Centered, with the cutoff date in the same amber badge the header
      // uses. The cashier reads this while holding an ID, so the date is
      // the one thing that has to survive a glance -- plain sentences all
      // at one weight made them hunt for it.
      message: (
        <div className="text-center">
          <p className="text-lg font-bold text-gray-900">
            Age restriction: {ageRestriction.age}
          </p>
          <p className="mt-3 text-gray-600">
            Have you completed ID verification?
          </p>
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-3 bg-amber-100 rounded-xl">
            <CalendarCheck size={18} className="text-amber-700 shrink-0" />
            <span className="text-amber-900">
              Born on or before{' '}
              <strong className="font-bold">{ageRestriction.cutoffLabel}</strong>
            </span>
          </div>
        </div>
      ),
      confirmLabel: 'Yes, ID Checked',
      cancelLabel: 'Cancel',
      variant: 'danger',
      onConfirm: () => {
        setAgeChecked(true)
        proceedToCheckout()
      },
    })
  }

  // Customer-insight cross-links: shop a brand / category, or jump straight
  // into a product detail. These are reused by both the route-level
  // CustomerModal (opened from the receipt sidebar) and the one rendered
  // inside the Customers tab.
  const handleShopByBrand = useCallback(
    (brandName: string) => {
      // Customer activity logs surface brand *names*; the BE filter keys
      // by id. Look the name up against the brand list and skip the
      // filter (still navigate) if it isn't recognized — better than
      // sending an empty filter that returns the whole catalog.
      const lower = brandName.toLowerCase()
      const match = brands.find((b) => b.name.toLowerCase() === lower)
      setUIFilters({ ...DEFAULT_UI_FILTERS, brands: match ? [match.id] : [] })
      setSelectedCategory(null)
      setSearchQuery('')
      setActiveTab('products')
    },
    [brands, setUIFilters, setSelectedCategory, setSearchQuery, setActiveTab],
  )

  const handleShopByCategory = useCallback(
    (categoryName: string) => {
      const lower = categoryName.toLowerCase()
      const match = categories.find((c) => c.name.toLowerCase() === lower)
      setUIFilters(DEFAULT_UI_FILTERS)
      if (match) {
        setSelectedCategory(match.id)
        setSearchQuery('')
      } else {
        // Fall back to a search if we can't resolve the category id.
        setSelectedCategory(null)
        setSearchQuery(categoryName)
      }
      setActiveTab('products')
    },
    [categories, setUIFilters, setSelectedCategory, setSearchQuery, setActiveTab],
  )

  const handleOpenProduct = useCallback((product: Product) => {
    setSelectedProduct(product)
  }, [])

  // Cross-component "open the product modal" channel. CommandPalette
  // (mounted in the Header, outside this route's tree) and any other
  // component that wants to open the product modal pushes a Product
  // through `uiStore.requestProductModal`; we mirror it into the
  // route's local `selectedProduct` and clear the request immediately
  // so the next push fires again.
  const productModalRequest = useUIStore((s) => s.productModalRequest)
  const clearProductModalRequest = useUIStore((s) => s.clearProductModalRequest)
  // Set when the next selectedProduct change came from a request that
  // explicitly opted out of recent-tracking (Recent panel clicks). The
  // push-to-recent effect reads-and-clears it.
  const skipNextRecentPushRef = useRef(false)
  useEffect(() => {
    if (!productModalRequest) return
    if (productModalRequest.trackRecent === false) {
      skipNextRecentPushRef.current = true
    }
    setSelectedProduct(productModalRequest.product)
    clearProductModalRequest()
  }, [productModalRequest, clearProductModalRequest])

  // Track every product modal open as a "recent product" for the
  // LeftSidebar Recent panel. Single observation point — anything that
  // sets `selectedProduct` (grid, palette, customer modal, scan)
  // funnels through here.
  const pushRecentProduct = useRecentProductsStore((s) => s.pushRecentProduct)
  useEffect(() => {
    if (!selectedProduct) return
    if (skipNextRecentPushRef.current) {
      skipNextRecentPushRef.current = false
      return
    }
    pushRecentProduct(selectedProduct)
  }, [selectedProduct, pushRecentProduct])

  // Global barcode / QR scanner router.
  //
  // USB scanners and most QR readers are keyboard wedges — they "type"
  // the captured code fast and end with Enter. `useBarcodeScanner`
  // handles the timing detection; this callback is the dispatcher.
  //
  // Routing rules:
  //   1. Explicit `CUST:` (or `cust:`) prefix → strip it and treat the
  //      remainder as a customer lookup. Lets the member-app QR
  //      generator force the customer path even if a product happens
  //      to share the same code.
  //   2. Otherwise try product first (the common counter case), then
  //      fall back to customer search on a miss. Reuses the same query
  //      keys the catalog uses so a re-scan within ~10s is a cache hit.
  //
  // Disabled while the checkout modal is open — mid-payment isn't the
  // time for a stray scan to silently mutate the cart — and while the
  // gift card sell modal is open, which claims scans for its card
  // number field. Other modals (held receipts, lot select, etc.) leave
  // the scanner armed because adding a line during those flows is
  // harmless and sometimes intentional.
  // No useCallback — useBarcodeScanner captures onScan via a ref so a
  // fresh closure per render doesn't re-bind the listener. Keeping it
  // as a plain function lets us close over `handleAddToCart` (which
  // isn't itself memoized) without dependency-array gymnastics.
  const customerIdForScan = selectedCustomer?.id ?? null
  // `meta` arrives from the keyboard-wedge path only (camera scans in
  // ScanModal call this bare) — it carries the scan-time CapsLock state
  // for lot-casing recovery.
  const handleScan = async (rawCode: string, meta?: ScanMeta) => {
    const code = rawCode.trim()
    if (!code) return

    // Receipt QR: V2 prints encode `RCP-<receipt_number>` in a footer QR.
    // Resolve to a receipt id and open the order modal. The lookup is
    // store-scoped, so a QR from another store 404s — surface that as a
    // not-found toast rather than falling through to the product/customer
    // search paths (a 14-char receipt number won't match either anyway).
    if (code.startsWith('RCP-')) {
      try {
        const result = await lookupReceiptByNumber(code)
        if (!result) {
          showToast({
            type: 'error',
            title: 'Receipt Not Found',
            message: `No receipt matches "${code}".`,
          })
          return
        }
        setOpenReceipt({ receiptId: result.receipt_id, number: result.number })
      } catch (err) {
        console.warn('[scan] receipt lookup failed', err)
        showToast({
          type: 'error',
          title: 'Lookup Failed',
          message: `Couldn't look up receipt ${code}. Please try again.`,
        })
      }
      return
    }

    const isExplicitCustomer = /^cust:/i.test(code)
    const query = isExplicitCustomer ? code.replace(/^cust:/i, '').trim() : code
    if (!query) return

    // GS1 pre-parse for cannabis combined-barcode-and-lot scans. A real
    // cannabis scan like `010062824280438710WGS13200418` carries both
    // the product GTIN and the batch number; we extract them here and
    // (a) look up by GTIN instead of the full 38-char string, (b) thread
    // the lot through to handleAddToCart so the cart line pins the
    // scanned batch instead of the FIFO guess. See `src/lib/gs1.ts`.
    //
    // Falls back to null on plain UPCs, customer QR codes, etc. — the
    // existing raw lookup handles those. Skipped entirely for explicit
    // CUST: prefix; that path forces a customer query regardless.
    const gs1 = isExplicitCustomer ? null : parseGs1Barcode(query)
    const lookupCode = gs1?.gtin ?? query

    // Diagnostic logging — helps trace why a scan didn't behave as
    // expected (no toast, wrong toast, etc.). Cheap and low-volume
    // (one set per scan); leave in until the scan flow has soaked.
    // Raw text DEV-only: non-GS1 codes can be customer identifiers
    // (CUST: QRs, phone lookups) and this ships to PostHog via replay
    // console capture. GS1 fields are product data, always safe — and
    // the product-path logs below echo matched codes on every outcome,
    // so prod replays lose nothing for product-scan debugging.
    const scanTextForLog = (s: string) =>
      import.meta.env.DEV ? s : `[${s.length} chars]`
    console.log('[scan] received', {
      code: scanTextForLog(code),
      isExplicitCustomer,
      query: scanTextForLog(query),
      gs1,
      lookupCode: gs1 ? lookupCode : scanTextForLog(lookupCode),
    })

    // Scan-to-confirm-lot router. When a hold/ecom order was loaded with
    // frontend.on_hold_scan_to_confirm_lot on, its cannabis lines load as
    // scan_confirmed:false and a scan must CONFIRM the matching line (stamping
    // the GS1 lot) instead of adding a row. Returns true when the scan was
    // consumed — as a confirmation, or as a "not on this order" warning — so
    // the caller must NOT add to cart; false when confirm mode is inactive.
    // Shared by BOTH the direct product-match path and the ambiguous-barcode
    // picker's onPick so neither bypasses the gate and deadlocks checkout.
    // Reads cart via getState() at call time — onPick fires after the picker,
    // so it must see current state, not the state at scan time.
    const tryConfirmScan = (product: Product): boolean => {
      const { cart, confirmScanLine } = usePOSStore.getState()
      if (!cart.some((l) => l.scan_confirmed === false)) return false

      // An order can split one SKU across several lots/quantities, so pick the
      // unconfirmed line whose lot matches the scan (keeps the lot↔qty pairing)
      // rather than the first — see pickScanConfirmTarget.
      const unconfirmed = cart.filter(
        (l) => l.scan_confirmed === false && l.product_id === product.id,
      )
      const scannedLot = gs1?.lot_number ?? null
      const target = pickScanConfirmTarget(unconfirmed, scannedLot, meta?.capsLock)
      if (!target) {
        showToast({
          type: 'warning',
          title: 'Not on This Order',
          message: `${product.display_name || product.description || 'That item'} isn't a lot waiting to be confirmed.`,
        })
        return true
      }
      // The matching scan confirms the line — presence is the gate. A GS1 scan
      // stamps the physical lot (AI 10); a plain UPC confirms and keeps the
      // FIFO-assigned lot. Either clears this line. When the scan matched the
      // line's own lot case-tolerantly, stamp the line's STORED casing —
      // restamping the raw scan would overwrite the journal casing the order
      // loaded with, with a CapsLock-mangled one (see findLotIgnoreCase).
      const stampLot =
        scannedLot !== null
        && target.lot_number !== null
        && target.lot_number.toLowerCase() === scannedLot.toLowerCase()
          ? target.lot_number
          : scannedLot
      confirmScanLine(target.id, stampLot)
      const remaining = cart.filter(
        (l) => l.scan_confirmed === false && l.id !== target.id,
      ).length
      showToast({
        type: 'success',
        title: 'Lot Confirmed',
        message:
          remaining > 0
            ? `${product.display_name || product.description || 'Item'} — ${remaining} more to scan.`
            : `${product.display_name || product.description || 'Item'} — all lots confirmed.`,
      })
      return true
    }

    // 1. Product lookup (skipped when the prefix forces customer).
    //
    // Strict barcode endpoint: 200 on exact match, 404 on miss, 409 on
    // catalog conflict (same code on multiple products). The BE is
    // authoritative on the match — we trust the 200 and add to cart.
    //
    // The BE echoes the scanned code back as `matched_barcode`
    // (lowercased + trimmed) on every 200. We compare against the
    // normalized scanned code purely as a pipeline-health alarm: a
    // mismatch would indicate (a) the request hit a different handler
    // than `products_by_barcode.py` (deploy skew, route
    // misregistration, stale cache layer) or (b) some mid-pipeline
    // mutation we don't expect. It does NOT block the cart-add — the
    // BE is still the source of truth on whether the code matched.
    // Track whether the lookup confirmed a 404 vs hit some other error.
    // Used below for the GS1-specific error message: a confirmed 404
    // ("no such product") deserves a different toast from a network /
    // 5xx failure ("we couldn't reach the catalog"). Both still skip
    // the customer fallback when GS1 was detected — a 14-digit GTIN
    // isn't going to match a customer either way.
    let lookupConfirmedMiss = false
    let lookupError: unknown = null
    if (!isExplicitCustomer) {
      try {
        const product = await queryClient.fetchQuery({
          queryKey: productKeys.barcode(lookupCode, customerIdForScan),
          queryFn: async (): Promise<Product | null> => {
            const params: Record<string, unknown> = { code: lookupCode }
            if (customerIdForScan) params.customer_id = customerIdForScan
            try {
              return await v2api.get<Product>(v2('/products/by-barcode'), params)
            } catch (err) {
              if (err instanceof V2ApiError && err.status === 404) return null
              throw err
            }
          },
          staleTime: 1000 * 10,
        })
        console.log('[scan] product lookup', {
          hit: !!product,
          id: product?.id,
          name: product?.display_name ?? product?.description,
          matched_barcode: product?.matched_barcode,
        })
        if (product) {
          // matched_barcode is the BE's echo of what it matched against,
          // so we compare to lookupCode (the GTIN we sent) rather than
          // the raw scan string. For non-GS1 scans lookupCode === query.
          const normalizedLookup = lookupCode.trim().toLowerCase()
          if (product.matched_barcode == null) {
            console.error('[scan] response missing matched_barcode echo', {
              scanned: query,
              lookup: lookupCode,
              returned: { id: product.id, name: product.display_name ?? product.description },
            })
          } else if (product.matched_barcode !== normalizedLookup) {
            console.error('[scan] matched_barcode echo disagrees with lookup code', {
              scanned: query,
              lookup: lookupCode,
              normalized: normalizedLookup,
              echo: product.matched_barcode,
              returned: { id: product.id, name: product.display_name ?? product.description },
            })
          }
          // Scan-to-confirm-lot: in confirm mode this consumes the scan as a
          // lot confirmation (see tryConfirmScan) rather than adding a row.
          if (tryConfirmScan(product)) return

          // Gate the success toast on the actual outcome: handleAddToCart
          // returns false when blocked (e.g. partial-payment lock), and
          // already surfaced its own warning toast in that case. Without
          // this gate the cashier sees both the block warning AND a
          // contradictory "Added to Cart" success.
          const added = await handleAddToCart(product, gs1?.lot_number ?? null, meta?.capsLock)
          if (added) {
            showToast({
              type: 'success',
              title: 'Added to Cart',
              message: product.display_name || product.description || `Product ${product.id}`,
            })
          }
          return
        }
        // fetchQuery resolved with null → BE returned 404 cleanly.
        lookupConfirmedMiss = true
      } catch (err) {
        // 409 ambiguous_barcode: BE returns full Product candidates that
        // share this code. When 2+ have store-level on-hand we surface a
        // picker modal so the cashier can tap the right one; the BE has
        // already narrowed to stocked candidates in that case, so any
        // candidate the FE sees with on_hand > 0 is one of the stocked
        // set. When none are stocked (returns / receive flow) we fall
        // back to the toast -- the picker would just be visual noise
        // when the cashier already knows they're working off-catalog.
        if (err instanceof V2ApiError && err.status === 409 && err.code === 'ambiguous_barcode') {
          // Array.isArray guards against a malformed BE response (null,
          // missing, wrong type) -- a broken BE shouldn't crash the
          // scan handler, just fall through to the toast path.
          const candidates: Product[] = Array.isArray(err.candidates)
            ? (err.candidates as Product[])
            : []
          // on_hand can be Decimal-serialized as a string by the BE, so
          // we coerce before comparing. `Number.isFinite` filters NaN
          // and Infinity defensively. Unstocked / non-numeric -> 0.
          const stockedCount = candidates.reduce((n, c) => {
            const oh = typeof c.on_hand === 'number'
              ? c.on_hand
              : parseFloat(String(c.on_hand ?? ''))
            return Number.isFinite(oh) && oh > 0 ? n + 1 : n
          }, 0)
          console.warn('[scan] ambiguous barcode', {
            query, lookup: lookupCode,
            total: candidates.length, stocked: stockedCount,
          })
          if (stockedCount >= 2) {
            openModal('barcode-match', {
              candidates,
              scannedCode: lookupCode,
              onPick: async (product: Product) => {
                // Route through the same scan-confirm gate as the direct-match
                // path. Without this, picking a candidate while lines are
                // pending would add a row and never confirm them — deadlocking
                // checkout. getState() inside reads the cart as of pick time.
                if (tryConfirmScan(product)) return
                // Await the add so we can gate the success toast on the
                // actual outcome -- partial-payment lock returns false
                // and surfaces its own warning. Was `void` fire-and-
                // forget previously; the await is required for correct
                // toast behavior, no other downstream consequence.
                const added = await handleAddToCart(product, gs1?.lot_number ?? null, meta?.capsLock)
                if (added) {
                  showToast({
                    type: 'success',
                    title: 'Added to Cart',
                    message:
                      product.display_name || product.description || `Product ${product.id}`,
                  })
                }
              },
            })
            return
          }
          showToast({
            type: 'warning',
            title: 'Catalog Conflict',
            message: `Code "${lookupCode}" matches multiple products. Search by SKU or name to pick one.`,
          })
          return
        }
        console.warn('[scan] product lookup failed', err)
        lookupError = err
      }

      // GS1-parsed lookup that didn't resolve to a product: skip the
      // customer fallback regardless of whether it was a 404 or an
      // error. A 14-digit GTIN won't match a customer either way, and
      // chaining the customer lookup just delays the toast (and risks
      // a freak phone-number-prefix match). The toast wording differs
      // by failure mode so cashiers can tell "real 404" from "BE down."
      if (gs1) {
        if (lookupConfirmedMiss) {
          console.log('[scan] GS1 product not found; skipping customer fallback', { gtin: gs1.gtin })
          showToast({
            type: 'error',
            title: 'Item Not Found',
            message: `No product found for GTIN ${gs1.gtin}.`,
          })
        } else {
          console.warn('[scan] GS1 lookup failed; skipping customer fallback', {
            gtin: gs1.gtin,
            err: lookupError,
          })
          showToast({
            type: 'error',
            title: 'Lookup Failed',
            message: `Couldn't look up GTIN ${gs1.gtin}. Please try again.`,
          })
        }
        return
      }
    }

    // 2. Customer lookup. We only auto-apply on an exact single
    //    match — multiple matches mean the scanner string was
    //    ambiguous (e.g. a phone-number prefix that hits several
    //    profiles), and silently picking the first would be worse
    //    than asking the cashier to disambiguate manually.
    let customers: Customer[]
    try {
      const data = await v2api.get<{ customers?: Customer[] } | Customer[]>(
        v2('/customers'),
        { q: query },
      )
      customers = Array.isArray(data) ? data : data?.customers ?? []
      console.log('[scan] customer lookup', { count: customers.length, ids: customers.slice(0, 5).map((c) => c.id) })
    } catch (err) {
      // Message only — a network-level AxiosError's toJSON embeds
      // config.url including ?q=<scanned customer identifier>, and this
      // ships to PostHog via replay console capture.
      console.warn('[scan] customer lookup failed', err instanceof Error ? err.message : err)
      customers = []
    }

    if (customers.length === 1) {
      // Attach in a dedicated try -- a successful lookup followed by a
      // failed attach (partial_payment_locked, cannot_cancel_paid,
      // blocked customer, etc.) must NOT fall through to the
      // "Customer Not Found" branch below. The customer was found;
      // the attach is what failed, and useSelectCustomer's own
      // toast/onError handler has already surfaced the reason.
      // Real production trigger: scanning a customer barcode while
      // the cart has a partial payment applied -- the hook throws
      // V2ApiError(409, 'partial_payment_locked') AND fires the
      // "Receipt has payment(s) applied" toast; without this guard
      // the cashier ALSO sees a misleading "Customer Not Found".
      try {
        await selectCustomer.mutateAsync(customers[0])
      } catch (err) {
        console.warn('[scan] customer attach failed', err)
        return
      }
      showToast({
        type: customers[0].is_blocked ? 'warning' : 'success',
        title: customers[0].is_blocked ? 'Customer Blocked' : 'Customer Selected',
        message:
          customers[0].display_name ||
          customers[0].full_name ||
          `Customer ${customers[0].id}`,
      })
      return
    }
    if (customers.length > 1) {
      showToast({
        type: 'warning',
        title: 'Multiple Customers',
        message: `${customers.length} customers match "${query}". Search by name to pick one.`,
      })
      return
    }

    console.log('[scan] no match → firing not-found toast')
    showToast({
      type: 'error',
      title: isExplicitCustomer ? 'Customer Not Found' : 'Item Not Found',
      message: isExplicitCustomer
        ? `No customer matches "${query}".`
        : `That item wasn't found. Scanned: ${code}`,
    })
  }

  useBarcodeScanner({
    onScan: handleScan,
    // Yield to the camera scanner while it's open so a Bluetooth HID wedge
    // can't also fire handleScan and double-add. One scan path at a time.
    // Also fully disabled under the clerk re-PIN overlay (which is NOT an
    // activeModal): the overlay swallows keys itself, but the scanner
    // buffer must not accumulate PIN digits either.
    // Gift card sell mounts its own scanner (a scan there is a card
    // number, never a SKU) — yield to it so one scan can't do both.
    enabled:
      activeModal !== 'checkout'
      && activeModal !== 'gift-card-sell'
      && !scannerOpen
      && clerkReauthRequired === null,
  })

  return (
    <Layout>
      <Header />

      <div className="flex-1 flex overflow-hidden">
        {!isMobile && <LeftSidebar onProductClick={handleAddToCartWrapper} />}

        <main className="flex-1 overflow-hidden flex flex-col bg-gray-50 transition-all duration-300 ease-out">
          <TabsBar />

          {activeTab === 'products' && (
            <>
              <CategoriesBar />
              <ProductFiltersBar />
              <div className="flex-1 overflow-y-auto p-2 md:p-4">
                {isLoading && products.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (
                  <ProductGrid
                    products={products}
                    onAddToCart={handleAddToCart}
                    onProductClick={handleProductClick}
                    viewMode={viewMode}
                    cartQuantities={cartQuantities}
                    hasNextPage={hasNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    fetchNextPage={fetchNextPage}
                  />
                )}
              </div>
            </>
          )}

          {activeTab === 'customers' && (
            <CustomersTab
              onShopByBrand={handleShopByBrand}
              onShopByCategory={handleShopByCategory}
              onOpenProduct={handleOpenProduct}
            />
          )}

          {activeTab === 'orders' && (
            <OrdersTab onOpenCustomer={setDetailsCustomer} />
          )}

          {activeTab === 'functions' && <FunctionsTab />}

          {activeTab === 'cart' && isMobile && (
            <RightSidebar
              customer={selectedCustomer}
              cart={cart}
              onChangeCustomer={handleChangeCustomer}
              onOpenCustomerDetails={setDetailsCustomer}
              onQuantityChange={cartChangeQuantity}
              onRemoveFromCart={cartRemoveFromCart}
              onCheckout={handleCheckout}
              onProductClick={handleProductClick}
              isMobileFullScreen
            />
          )}

          {/* Mobile-only bottom action bar: cart count + total (tap -> cart)
              plus a camera-scan button (scan -> add to cart). Self-gates to
              mobile + non-cart tab; a flex child so it sits below the scrolling
              content without overlapping it. */}
          <MobileCartBar onScanClick={() => setScannerOpen(true)} />
        </main>

        {!isMobile && (
          <RightSidebar
            customer={selectedCustomer}
            cart={cart}
            onChangeCustomer={handleChangeCustomer}
            onOpenCustomerDetails={setDetailsCustomer}
            onQuantityChange={cartChangeQuantity}
            onRemoveFromCart={cartRemoveFromCart}
            onCheckout={handleCheckout}
            onProductClick={handleProductClick}
          />
        )}
      </div>

      {/* Product Modal */}
      {selectedProduct && (
        <ProductModal
          product={{...selectedProduct, cartItemId: selectedProduct.cartItemId}}
          onClose={() => setSelectedProduct(null)}
        />
      )}

      {/* Customer Details Modal */}
      {detailsCustomer && (
        <CustomerModal
          customer={detailsCustomer}
          initialTab={detailsInitialTab}
          onClose={() => setDetailsCustomer(null)}
          onShopByBrand={handleShopByBrand}
          onShopByCategory={handleShopByCategory}
          onOpenProduct={handleOpenProduct}
        />
      )}

      {/* Order Detail Modal — opened by scanning an RCP- receipt QR. */}
      {openReceipt && (
        <OrderDetailModal
          receiptId={openReceipt.receiptId}
          receiptNumber={openReceipt.number}
          hideAddActions
          onClose={() => setOpenReceipt(null)}
        />
      )}

      {/* Camera barcode scanner (mobile). Driven by its own boolean rather than
          activeModal so it can open over the products grid; decodes go to
          handleScan — the same barcode -> add-to-cart path as the wedge. */}
      {scannerOpen && (
        <ScanModal onScan={handleScan} onClose={() => setScannerOpen(false)} />
      )}

      {/* Modals */}
      {activeModal === 'checkout' && <CheckoutModal />}
      {activeModal === 'age-verify' && <AgeVerifyModal />}
      {activeModal === 'lot-select' && <LotSelectModal />}
      {activeModal === 'held-receipts' && <HeldReceiptsModal />}
      {activeModal === 'void-receipt' && <VoidReceiptModal />}
      {activeModal === 'discount' && <DiscountModal />}
      {activeModal === 'payout' && <PayoutModal />}
      {activeModal === 'gift-card-sell' && <GiftCardSellModal />}
      {activeModal === 'barcode-match' && <BarcodeMatchModal />}

      {/* ConfirmModal sits on its own layer (driven by `confirmOptions`,
          not `activeModal`) so it can stack on top of the CheckoutModal,
          customer modals, etc. without unmounting whatever was below.
          Canceling the confirm leaves the underlying modal untouched. */}
      {confirmOptions && <ConfirmModal />}

      {/* Offline Status */}
      <OfflineIndicator />
    </Layout>
  )
}

